import moment from 'moment'
import _ from 'underscore'
import Brand from 'helpers/brand'

import Environment from 'helpers/environment'

import Honeybadger from 'honeybadger-js'

export const precision = (value, precision) => scrubNonNumber(value, () => formatNumericalUnit(value, '', precision))
export const defaultIfOutrageous = (value) => scrubNonNumber(value, () => value)

/* eslint-disable no-multi-spaces */
const unitFormats = [
  { name: 'amps',                    unit: 'A',     spaceBetween: true,  defaultPrecision: 1 },
  { name: 'cubicFeetPerMinute',      unit: 'CFM',   spaceBetween: true                       },
  { name: 'degrees',                 unit: '°',     spaceBetween: false                      },
  { name: 'degreesFahrenheit',       unit: '°F',    spaceBetween: false, defaultPrecision: 1 },
  { name: 'feet',                    unit: 'ft',    spaceBetween: true                       },
  { name: 'microgramsPerCubicMeter', unit: 'µg/m³', spaceBetween: true,  defaultPrecision: 1 },
  { name: 'partsPerBillion',         unit: 'ppb',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'partsPerMillion',         unit: 'ppm',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'percentage',              unit: '%',     spaceBetween: false                      },
  { name: 'pressure',                unit: 'PSIG',  spaceBetween: true,  defaultPrecision: 1 },
  { name: 'relativeHumidity',        unit: '% RH',  spaceBetween: false, defaultPrecision: 0 },
  { name: 'rpm',                     unit: 'RPM',   spaceBetween: true                       },
  { name: 'rps',                     unit: 'RPS',   spaceBetween: true                       },
  { name: 'staticPressure',          unit: 'inWC',  spaceBetween: true,  defaultPrecision: 2 },
  { name: 'voltageAC',               unit: 'VAC',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'voltageDC',               unit: 'VDC',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'watts',                   unit: 'W',     spaceBetween: true,  defaultPrecision: 1 },
  { name: 'number',                  unit: '',      spaceBetween: false                      }
]
/* eslint-enable no-multi-spaces */

export const formatForUnit = (value, unit, precision = null) => {
  const format = unitFormats.find(f => f.unit === unit)

  if (format) {
    return scrubNonNumber(value, () => {
      const precisionToUse = precision ?? format.defaultPrecision ?? 0
      const valueToUse = format.valueTransform ? format.valueTransform(value) : value
      const unitToUse = format.spaceBetween ? ` ${unit}` : unit

      return formatNumericalUnit(valueToUse, unitToUse, precisionToUse)
    })
  } else {
    if (!Environment.isDevelopment()) {
      Honeybadger.notify(`No formatter found for unit: ${unit}`)
      return defaultIfOutrageous(value)
    } else {
      throw new Error(`No formatter found for unit: ${unit}`)
    }
  }
}

const buildUnitFormatter = (name) => (value, precision) => formatForUnit(value, unitFormats.find(f => f.name === name).unit, precision)

export const amps = buildUnitFormatter('amps')
export const cubicFeetPerMinute = buildUnitFormatter('cubicFeetPerMinute')
export const degrees = buildUnitFormatter('degrees')
export const degreesFahrenheit = buildUnitFormatter('degreesFahrenheit')
export const feet = buildUnitFormatter('feet')
export const microgramsPerCubicMeter = buildUnitFormatter('microgramsPerCubicMeter')
export const partsPerBillion = buildUnitFormatter('partsPerBillion')
export const partsPerMillion = buildUnitFormatter('partsPerMillion')
export const percentage = buildUnitFormatter('percentage')
export const pressure = buildUnitFormatter('pressure')
export const relativeHumidity = buildUnitFormatter('relativeHumidity')
export const rpm = buildUnitFormatter('rpm')
export const rps = buildUnitFormatter('rps')
export const staticPressure = buildUnitFormatter('staticPressure')
export const voltageAC = buildUnitFormatter('voltageAC')
export const voltageDC = buildUnitFormatter('voltageDC')
export const watts = buildUnitFormatter('watts')
export const number = buildUnitFormatter('number')

export const damperAngle = (value) => scrubNonNumber(value, () => formatNumericalUnit(((100 - precision(value, 0)) / 100) * 90, 'deg'))
export const onOff = (value) => scrubNull(value, () => { if (value === true) { return 'on' } else { return 'off' } })
export const ratioToPercent = (value) => scrubNonNumber(value, () => value ? percentage(Number(value) * 100) : value)
export const rpmToPwm = (value) => scrubNonNumber(value, () => formatNumericalUnit(Math.round(((Number(value) - 200) / 13.3) + 20), ' PWM', 1))
export const rpsToRpm = (value) => scrubNonNumber(value, () => formatNumericalUnit(Number(value) * 60, ' RPM', 0))

export const longDate = (date) => {
  return (
    new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
      .format(moment(date, 'YYYY-MM-DD').toDate())
  )
}

export const longDateTime = (dateTime) => moment(dateTime).format('llll')
export const shortTimeDate = (dateTime) => moment(dateTime).format('h:mm a M/D/YYYY')
export const shortDateTime = (dateTime) => moment(dateTime).format('MM/DD/YYYY h:mm A') // this has a non-breaking space before "A" so the time stays together
export const shortTime = (dateTime) => moment(dateTime).format('h:mm A')
export const shortDate = (dateTime) => moment(dateTime).format('MM/DD/YYYY')
export const longMonthYear = (dateTime) => moment(dateTime).format('MMMM YYYY')
export const longDateTimeWithSeconds = (dateTime) => moment(dateTime).format('ddd, MMM D, YYYY h:mm:ss A')
export const shortDateWithDayOfWeek = (dateTime) => moment(dateTime).format('ddd, MMM D, YYYY')
export const shortNumericDateWithDayOfWeek = (dateTime) => moment(dateTime).format('ddd M/D/YYYY')
export const shortMonthDay = (dateTime) => moment(dateTime).format('MMM D')
export const longDateWithDayOfWeekAndTime = (dateTime) => moment(dateTime).format('dddd, MMM D, YYYY h:mm:ss A')
export const monthFullName = (dateTime) => moment(dateTime).format('MMMM')

export const customerDisplayName = (firstName, lastName, companyName) => {
  if (Brand.isCommercial() && companyName) {
    return companyName
  } else {
    return _.compact([firstName, lastName]).join(' ')
  }
}

export const deviceTypeDisplayName = (deviceType) => {
  return deviceType === 'control_hub' ? 'system controller' : deviceType?.replace(/_/g, ' ')
}

export const phoneNumber = (number) => {
  if (!number) return null

  if (number?.length === 10) {
    return number?.replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, '($1) $2-$3') ?? ''
  } else if (number?.length === 11) {
    return number?.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4')
  } else {
    return number
  }
}

export const streetAddress = (address1, address2) => {
  return _.compact([address1, address2]).join(', ')
}

export const cityStateZip = (city, state, zip) => {
  return _.compact([city, _.compact([state, zip]).join(' ')]).join(', ')
}

export const fullOneLineAddress = (address, city, state, zip) => {
  return _.compact([address, city, _.compact([state, zip]).join(' ')]).join(', ')
}

export const friendlyName = (name, friendlyNames = {}) => {
  if (Object.keys(friendlyNames).includes(name)) {
    return friendlyNames[name]
  } else {
    return replace(name)
  }
}

export const replace = (str, find = '_', replace = ' ') => {
  return scrubNull(str, () => str.toString().replace(new RegExp(find, 'g'), replace))
}

const formatNumericalUnit = (value, symbol, precision = 1) => {
  return Number(value).toFixed(precision) + symbol
}

const scrubNonNumber = (value, formatter = null) => {
  if (scrubNull(value) === null || isNaN(value) || Number(value) === 32768 || Number(value) === 3276800) {
    return null
  } else if (formatter) {
    return formatter(value)
  }

  return value
}

export const scrubNull = (value, formatter = null) => {
  if (value === undefined || value === null || value === '') {
    return null
  } else if (formatter) {
    return formatter(value)
  }

  return value
}

export const removeScriptTags = (text) => {
  const div = document.createElement('div')
  div.innerHTML = text

  const scripts = div.getElementsByTagName('script')
  let i = scripts.length

  while (i--) {
    scripts[i].parentNode.removeChild(scripts[i])
  }

  return div.innerHTML
}
export const generateUniqueId = () => `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

export const capitalize = (string) => {
  if (!string || '') return string
  return string
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const camelToNormalCase = (string) => {
  if (!string || string === '') {
    return string
  } else {
    return string
      .split(/(?=[A-Z])/)
      .map(word => capitalize(word))
      .join(' ')
  }
}

export const installationStatus = (status) => status ? 'INSTALLED' : 'NOT INSTALLED'

export const formatStages = (stage) => {
  if (stage?.includes(' ')) return stage
  const match = stage?.match(/^stage(\d+)$/)
  if (match) return `${match[1]} Stage`
  return camelToNormalCase(stage) || undefined
}

export const mapToDropdownOptions = (map) =>
  Object.keys(map).map((key) => [{ value: String(map[key]).trim(), name: replace(key, '_', ' ') }])

export const isValidNumericInput = (value, maxDigits) =>
  new RegExp(`^\\d{1,${maxDigits}}(\\.\\d)?$`).test(value)

export const getErrorMessage = (label, value) => value ? undefined : `${label} is required`
